import { Stack } from '@mui/material';
import { orderBy } from 'lodash';
import { useMemo } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { getOptionsWithGroupLabels } from '@/components/form/baseInputs/SelectInput/SelectInput.utils';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import { Modal, ModalProps } from '@/components/modals/Modal/Modal';
import { PopperContent } from '@/components/poppers/PopperContent';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { STATE_CODE_TO_NAME } from '@/constants/states';
import { useAssetCategoryOptions } from '@/modules/entities/StructuredAssetsSubform/hooks/useAssetCategoryOptions';
import { STATE_TAX_SITUS_HELP_BODY } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.fields';
import {
  TAX_SITUS_GRANTOR_PREFIX,
  TAX_SITUS_STATE_PREFIX,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import {
  decodeSitusKind,
  getOwnerAwareDefaultTaxSitus,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import {
  getAssetDetailsModalSitusEventAction,
  useTrackAssetTaxSitusEvent,
} from '@/modules/entities/StructuredAssetsSubform/useTrackAssetTaxSitusEvent';
import { useUnguardedHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { AssetValueV2OwnershipType, StateCode } from '@/types/schema';

import { ASSET_QSBS_ELIGIBILITY_OPTIONS } from '../AssetQSBSEligibility';
import { Asset } from '../types/asset';
import { AssetCategoryField } from './AssetCategoryField';
import { AssetDetailsForm } from './AssetDetailsModal.types';

export type AssetDetailsModalProps = Omit<
  ModalProps,
  'children' | 'heading'
> & {
  asset: Asset;
  isNewAsset: boolean;
  onSubmit: (
    value: Asset,
    opts: { isRemoveAsset: boolean; isNewAsset: boolean }
  ) => void;
};

function generateInitialValues(asset: Asset): AssetDetailsForm {
  return {
    categoryId: asset.categoryId || '',
    displayName: asset.displayName,
    marketValue: asset.marketValue,
    qsbsEligibility: asset.qsbsEligibility,
    taxSitus: asset.taxSitus ?? '',
  };
}

function normalizeAssetForSave(
  assetId: string,
  originalAsset: Asset,
  formValues: AssetDetailsForm
): Asset {
  return {
    id: assetId,
    categoryId: formValues.categoryId,
    displayName: formValues.displayName,
    valueId: originalAsset.valueId,
    qsbsEligibility: formValues.qsbsEligibility,
    marketValue: formValues.marketValue,
    valuationMethod: AssetValueV2OwnershipType.ValueBased,
    // Carry through so submit can detect renames and reuse the existing AssetStatic.
    assetStaticID: originalAsset.assetStaticID,
    assetStaticDisplayName: originalAsset.assetStaticDisplayName,
    taxSitus: formValues.taxSitus,
    _originalTaxSitus:
      originalAsset._originalTaxSitus ?? originalAsset.taxSitus,
  };
}

function AssetDetailsModalInner({
  asset,
  onClose,
  onSubmit,
  isOpen,
  isNewAsset,
  ...modalProps
}: AssetDetailsModalProps) {
  const { options: assetCategoryOptions, loading: formOptionsLoading } =
    useAssetCategoryOptions();
  const { primaryClients, householdId } = useUnguardedHouseholdDetailsContext();
  const { assetClassesById } = useTenantDetailsContext();
  const trackAssetTaxSitusEvent = useTrackAssetTaxSitusEvent();

  const defaultTaxSitus = useMemo(() => {
    return getOwnerAwareDefaultTaxSitus({
      primaryClients,
      ownerPrincipalIds: undefined,
    });
  }, [primaryClients]);

  const initialValues: AssetDetailsForm = useMemo(() => {
    const base = generateInitialValues(asset);
    return base.taxSitus ? base : { ...base, taxSitus: defaultTaxSitus };
  }, [asset, defaultTaxSitus]);

  const formMethods = useForm<AssetDetailsForm>({
    defaultValues: initialValues,
  });
  const { control, handleSubmit, reset, formState } = formMethods;

  const taxSitusOptions = useMemo(() => {
    const sortedClients = orderBy(primaryClients ?? [], [
      (c) => c.createdAt,
      (c) => c.id,
    ]);
    const grantorOptions: SelectInputOption[] = sortedClients.map((client) => {
      const statePrefix = client.stateCode ? `${client.stateCode} - ` : '';
      return {
        value: `${TAX_SITUS_GRANTOR_PREFIX}${client.id}`,
        display: `${statePrefix}(${client.displayGivenName}'s resident state)`,
      };
    });
    const stateCodeOptions: SelectInputOption[] = (
      Object.keys(STATE_CODE_TO_NAME) as StateCode[]
    ).map((stateCode) => ({
      value: `${TAX_SITUS_STATE_PREFIX}${stateCode}`,
      display: stateCode,
    }));
    return getOptionsWithGroupLabels([
      { label: 'State of domicile', options: grantorOptions },
      { label: null, options: stateCodeOptions },
    ]);
  }, [primaryClients]);

  const onValidSubmit: SubmitHandler<AssetDetailsForm> = (values) => {
    onSubmit(normalizeAssetForSave(asset.id, asset, values), {
      isRemoveAsset: false,
      isNewAsset,
    });
    // Compare the submitted situs to the form's pre-filled default (not
    // asset.taxSitus) so accepting an autofilled default doesn't fire a
    // phantom 'created' event — true for both new assets and existing
    // assets that have no persisted situs (both autofill the default).
    const action = getAssetDetailsModalSitusEventAction({
      initialSitus: initialValues.taxSitus,
      persistedSitus: asset.taxSitus ?? '',
      currentSitus: values.taxSitus,
    });
    if (action) {
      trackAssetTaxSitusEvent(action, {
        source: 'asset_details_modal',
        householdId,
        assetClass: assetClassesById[values.categoryId]?.displayName,
        situsKind: decodeSitusKind(values.taxSitus),
      });
    }
    reset();
  };

  const onFormSubmit = handleSubmit(onValidSubmit);

  const modalActions = (
    <FormModalActions.Provider formState={formState}>
      {!isNewAsset && (
        <FormModalActions.DeleteButton
          confirmDeleteText="Delete asset"
          aria-label="Delete asset"
          onConfirmDelete={() =>
            onSubmit(asset, { isRemoveAsset: true, isNewAsset: false })
          }
        />
      )}
      <FormModalActions.CancelButton onClick={onClose} />
      <FormModalActions.SaveButton onClick={onFormSubmit}>
        {isNewAsset ? 'Add asset' : 'Save asset'}
      </FormModalActions.SaveButton>
    </FormModalActions.Provider>
  );

  return (
    <FormProvider {...formMethods}>
      <Modal
        isOpen={isOpen}
        actions={modalActions}
        onClose={onClose}
        {...modalProps}
        heading={'Asset details'}
      >
        <Stack
          component="form"
          noValidate
          onSubmit={onFormSubmit}
          flex={1}
          sx={{ minHeight: 450 }}
        >
          <FormLayoutRow>
            <FormLayoutItem>
              <FormFieldsDisabledProvider isDisabled={formOptionsLoading}>
                <AssetCategoryField options={assetCategoryOptions ?? []} />
              </FormFieldsDisabledProvider>
            </FormLayoutItem>
          </FormLayoutRow>

          <FormLayoutRow>
            <FormLayoutItem>
              <FormAwareTextInput<AssetDetailsForm>
                control={control}
                fieldName="displayName"
                required
                label="Asset name"
              />
            </FormLayoutItem>
          </FormLayoutRow>

          <FormLayoutRow>
            <FormLayoutItem width={8}>
              <FormAwareCurrencyInput<AssetDetailsForm>
                control={control}
                fieldName="marketValue"
                required
                isDecimalJSInput
                label="Market value"
              />
            </FormLayoutItem>
            <FormLayoutItem width={8}>
              <FormAwareSelectInput<AssetDetailsForm>
                control={control}
                testId="assetQsbsEligibility"
                fieldName="qsbsEligibility"
                required
                label="QSBS eligibility"
                options={ASSET_QSBS_ELIGIBILITY_OPTIONS}
              />
            </FormLayoutItem>
          </FormLayoutRow>

          <FormLayoutRow>
            <FormLayoutItem width={8}>
              <FormAwareSelectInput<AssetDetailsForm>
                control={control}
                fieldName="taxSitus"
                label="State"
                required
                showEmptyValue={false}
                options={taxSitusOptions}
                contextualHelp={
                  <PopperContent body={STATE_TAX_SITUS_HELP_BODY} />
                }
              />
            </FormLayoutItem>
          </FormLayoutRow>
        </Stack>
      </Modal>
    </FormProvider>
  );
}

export function AssetDetailsModal(modalProps: AssetDetailsModalProps) {
  return <AssetDetailsModalInner {...modalProps} />;
}
